import { Box, Grid } from "@mui/material";
import React from "react";
import { Formik, FormikHelpers } from "formik";
import { ScreenFlowFormButtons } from "./ScreenFlowFormButtons";
import { objectVals2String } from "../../../utils/objectVals2String";
import { IconButton } from "./IconButton";

interface Values {
  checked: string[];
}

export type SelectButton = {
  key: string;
  text: string;
  icon?: JSX.Element;
};

interface Props {
  initChecked?: string[];
  answerLabel?: string;
  selectButtons: SelectButton[];
  isOneRequired?: boolean;
  errorText?: string;
  noneOfAboveButtonKey?: string;
  onComplete: (checked: string[]) => Promise<boolean>;
  onBack?: () => void;
}

export const MultiSelectButtonForm = ({
  initChecked,
  selectButtons,
  isOneRequired,
  errorText,
  noneOfAboveButtonKey,
  onComplete,
  onBack,
}: Props): JSX.Element => {
  // https://formik.org/docs/api/formik
  const formikInit = {
    values: {
      checked: initChecked || [""],
    },
  };

  const handleSubmit = async (
    values: Values,
    { setSubmitting, setStatus }: FormikHelpers<Values>
  ) => {
    setStatus("");
    try {
      if (values.checked[0] === "") {
        values.checked.shift();
      }
      if (isOneRequired && values.checked?.length === 0) {
        setStatus(
          errorText || "Please select at least one option to continue."
        );
        setSubmitting(false);
      } else {
        await onComplete(values.checked);
      }
    } catch (error) {
      setStatus("Sorry, error: " + ((error as Error)?.message || ""));
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={formikInit.values}
      onSubmit={handleSubmit}
      key={objectVals2String(formikInit.values)}
      enableReinitialize
    >
      {({ setFieldValue, handleSubmit, isSubmitting, values, status }) => (
        <form onSubmit={handleSubmit}>
          <Grid
            container
            spacing={1}
            sx={{
              my: 1,
            }}
          >
            <Grid item xs={12}>
              <Box sx={{ display: "flex" }}>
                <Grid container spacing={2}>
                  {selectButtons?.map((selectButton: SelectButton) => (
                    <Grid item sm={4} xs={12} key={selectButton.key}>
                      <IconButton
                        text={selectButton.text}
                        icon={selectButton.icon}
                        selected={values.checked.includes(selectButton.key)}
                        onClick={(newSelection: boolean) => {
                          // Special use case: one button nullifies all the others
                          //  a "None of the above" option
                          // When selected all other should unselect
                          // When any others are selected it should unselect
                          const noneButton =
                            selectButton.key === noneOfAboveButtonKey;
                          if (noneButton && newSelection) {
                            noneOfAboveButtonKey &&
                              setFieldValue("checked", [noneOfAboveButtonKey]);
                          } else {
                            // Toggle the in-coming value
                            //  if newSelection is true add to array, otherwise remove it
                            let newChecked = newSelection
                              ? [...values.checked, selectButton.key]
                              : values.checked.filter(
                                  (value) => value !== selectButton.key
                                );
                            if (
                              noneOfAboveButtonKey &&
                              newChecked.length > 1 &&
                              values.checked.includes(noneOfAboveButtonKey)
                            ) {
                              newChecked = newChecked.filter(
                                (value) => value !== noneOfAboveButtonKey
                              );
                            }
                            setFieldValue("checked", newChecked);
                          }
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <ScreenFlowFormButtons
                isSubmitting={isSubmitting}
                status={status}
                onBack={onBack}
              />
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};
